import React, { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useLocation } from 'react-use';

import { useUserState } from 'hooks/useUser';
import { Paths } from 'components/utils/Routing';
import { UserClaims } from 'backend/types/userClaims';
import { fuego } from 'services/firebase/fuego';
import { trackEvent } from 'services/analytics';
import { ANALYTICS_EVENTS } from 'services/analytics/events';

export const Auth: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { push } = useHistory();
  const location = useLocation();
  const [user, setUser] = useUserState();
  const lastTrackedLoginUidRef = useRef<string | null>(null);

  useEffect(() => {
    const unsubscribe = fuego.auth().onIdTokenChanged(async (user) => {
      if (!user) {
        lastTrackedLoginUidRef.current = null;
        setUser(user);
        return;
      }

      try {
        let tokenResult = await user.getIdTokenResult();

        //waiting claims propagation
        if (!tokenResult.claims.scope) {
          trackEvent(ANALYTICS_EVENTS.CLAIMS_REFRESH_FORCED, {
            reason: 'missing_scope_claim',
          });
          await new Promise((res) => setTimeout(res, 1000));
          await user.getIdToken(true); // refresh du token
          tokenResult = await user.getIdTokenResult(); // claims good
        }

        const token = await user.getIdToken();
        setUser({
          ...user,
          token,
          claims: tokenResult.claims as UserClaims,
        });

        if (lastTrackedLoginUidRef.current !== user.uid) {
          const claims = tokenResult.claims as UserClaims;
          const coOwnerClaimsCount = claims.coOwnerIdsByGeneralAssemblyId
            ? Object.keys(claims.coOwnerIdsByGeneralAssemblyId).length
            : 0;
          const externalClaimsCount = claims.externalParticipantIn
            ? claims.externalParticipantIn.length
            : 0;

          trackEvent(ANALYTICS_EVENTS.LOGIN_SUCCESS, {
            scope: claims.scope || 'CO_OWNER',
            has_scope_claim: Number(Boolean(claims.scope)),
            coowner_claims_count: coOwnerClaimsCount,
            external_claims_count: externalClaimsCount,
          });
          lastTrackedLoginUidRef.current = user.uid;
        }
      } catch (error) {
        trackEvent(ANALYTICS_EVENTS.LOGIN_FAILED, {
          reason:
            error instanceof Error ? error.name || 'Error' : 'UnknownError',
        });
        setUser(null);
      }
    });

    return () => unsubscribe();
  }, [setUser]);

  useEffect(() => {
    if (
      user === null &&
      location.pathname !== Paths.LOGIN &&
      location.pathname !== Paths.REGISTRATED_LOGIN
    ) {
      const loginUrl = new URL([window.location.origin, Paths.LOGIN].join(''));

      const wasLoggedIn = localStorage.getItem('wasLoggedIn') === 'true';

      if (
        !wasLoggedIn &&
        location.pathname &&
        location.pathname !== '/' &&
        location.href &&
        location.origin
      ) {
        const b64PrevPath = btoa(location.href.replace(location.origin, ''));
        loginUrl.searchParams.append('prevPath', b64PrevPath);
      }

      localStorage.setItem('wasLoggedIn', 'false');

      push([loginUrl.pathname, loginUrl.search].join(''));
    }
  }, [user, push, location]);

  useEffect(() => {
    if (user?.claims) {
      localStorage.setItem('wasLoggedIn', 'true');
    }
  }, [user?.claims]);

  if (user === undefined) {
    return null;
  }

  return <>{children}</>;
};
